import { Stack, Tooltip, Typography } from '@mui/material';
import React, { memo, useContext, useMemo } from 'react';
import { GroupSettingsContext } from '../../../../../contexts/GroupSettingsContext';
import useConnectedApps from '../../../../../hooks/useConnectedApps';
import { ConnectedApp, Dashboard } from '../../../../../types/files';
import DynamicAppIcon from '../../../DynamicAppIcon';
import useStyles from "../styles";

const ConnectedAppsCell: React.FC<{ dashboard: Dashboard }> = ({ dashboard }) => {
    const classes = useStyles();
    const { getDisplayName } = useConnectedApps();
    const { connectedApps: groupConnectedApps } = useContext(GroupSettingsContext);

    // Derive the app list from the GLOBAL group settings, enriched with
    // per-dashboard links when available.  This ensures every row always
    // shows exactly the apps configured at the group level.
    const apps = useMemo((): ConnectedApp[] => {
        return groupConnectedApps.map(name => {
            const dashEntry = dashboard?.connectedApps?.find(a =>
                (a.name || '').toLowerCase() === name.toLowerCase()
            );
            return { name, link: dashEntry?.link ?? null };
        });
    }, [groupConnectedApps, dashboard]);

    if (!apps.length) {
        return (
            <Typography className={classes.sublabel} pl={1}>
                {'—'}
            </Typography>
        );
    }

    return (
        <Stack direction="row" spacing={1.5} alignItems="center">
            {apps.map((app, i) => (
                <Tooltip key={`${app.name}-${i}`} title={getDisplayName(app)}>
                    <Stack
                        sx={{ cursor: app.link ? 'pointer' : 'default' }}
                        onClick={() => {
                            if (app.link) {
                                const url = /^https?:\/\//i.test(app.link.trim()) ? app.link.trim() : `https://${app.link.trim()}`;
                                window.open(url, '_blank', 'noopener,noreferrer');
                            }
                        }}>
                        <DynamicAppIcon link={app.link} name={app.name} size={20} showBackground={false} grayscale={!app.link} />
                    </Stack>
                </Tooltip>
            ))}
        </Stack>
    );
}

export default memo(ConnectedAppsCell);
